import PropTypes from 'prop-types';
import { createContext, useContext, useState } from 'react';
import { focusOnElement, removeExistingFocus } from '@utils/focus-on-element';
import { getPageHeadingsTree, keyForNode } from '@utils/page-headings';
import { calculateStats, validateHeadings } from '@utils/validate-headings';

export const HeadingStructureContext = createContext({});

export const HeadingStructureContextProvider = ({ children }) => {
	/** Important to keep it true by default. */
	const loadingState = useState(true);
	const pageHeadingsState = useState([]);
	const pageHeadingValidationStatsState = useState({});
	const expandedKeyState = useState(null);

	return (
		<HeadingStructureContext.Provider
			value={{
				loadingState,
				pageHeadingsState,
				pageHeadingValidationStatsState,
				expandedKeyState,
			}}
		>
			{children}
		</HeadingStructureContext.Provider>
	);
};

HeadingStructureContextProvider.propTypes = {
	children: PropTypes.node,
};

export const useHeadingStructureContext = () => {
	const {
		loadingState,
		pageHeadingsState,
		pageHeadingValidationStatsState,
		expandedKeyState,
	} = useContext(HeadingStructureContext);
	const [isLoading, setIsLoading] = loadingState;
	const [pageHeadings, setPageHeadings] = pageHeadingsState;
	const [validationStats, setValidationStats] = pageHeadingValidationStatsState;
	const [expandedKey, setExpandedKey] = expandedKeyState;

	const updateHeadingsTree = () => {
		const updatedHeadings = validateHeadings(getPageHeadingsTree());

		setPageHeadings(updatedHeadings);
		setValidationStats(calculateStats(updatedHeadings));

		setIsLoading(false);
	};

	const isHeadingExpanded = (node) => {
		return keyForNode(node) === expandedKey;
	};

	const toggleHeading = (node) => {
		const key = keyForNode(node);

		setExpandedKey((prev) => {
			if (prev === key) {
				removeExistingFocus();

				return null;
			} else {
				focusOnElement(node);

				return key;
			}
		});
	};

	const collapseHeading = () => {
		setExpandedKey(null);
	};

	return {
		isLoading,
		pageHeadings,
		validationStats,
		setPageHeadings,
		expandedKey,
		updateHeadingsTree,
		isHeadingExpanded,
		toggleHeading,
		collapseHeading,
	};
};
